/**
 * OP Readiness API client + types (Stage 7a — Publishing Nexus UI).
 * Read-only consumers of /api/op-readiness/* (canonical mart/sync tables, DEV-only today).
 */
import { apiRequest } from './api';

export interface CanonicalUnavailable {
  available: false;
  reason: string;
}

export interface ReadinessRollupRow {
  ReadinessStatus: string;
  CanWrite: boolean;
  BlockedReasonCode: string | null;
  Products: number;
}

export interface AuditRunRow {
  AuditRunId: string;
  ProjectionVersion?: string;
  Status: string;
  StartedAt: string;
  CompletedAt: string | null;
  TotalRecords: number;
  ReadyCount: number;
  BlockedCount: number;
  MismatchCount: number;
  ParityPct: number | null;
  HasItems?: number;
}

export interface ItemStatusCount {
  AuditStatus: string;
  Products: number;
}

export interface FieldSummaryRow {
  FieldPath: string;
  Severity: 'critical' | 'warning' | 'info';
  ComparisonMode: string;
  MismatchCount: number;
  ExampleExpected: string | null;
  ExampleActual: string | null;
  AuditRunId?: string;
}

export interface ReadinessSummary {
  available: true;
  projectionVersion: string;
  rollup: ReadinessRollupRow[];
  auditRuns: AuditRunRow[];
  latestItemStatuses: ItemStatusCount[];
  topMismatchFields: FieldSummaryRow[];
  prices: { PriceRows: number; PriceProducts: number };
}

export interface ProductRow {
  OPProductPayloadId: string;
  ProductId: string;
  EAN: string | null;
  Title: string | null;
  Language: string | null;
  ReadinessStatus: string;
  CanWrite: boolean;
  BlockedReasonCode: string | null;
  ProjectedAt: string;
  AuditStatus: string | null;
  AuditMismatchCount: number | null;
  OPProductId: number | null;
  LastAuditedAt: string | null;
}

export interface ProductsResponse {
  available: true;
  total: number;
  page: number;
  pageSize: number;
  rows: ProductRow[];
}

export interface FieldResult {
  field: string;
  severity: string;
  mode: string;
  match: boolean;
  expected: string;
  actual: string;
}

export interface DispatchHistoryRow {
  DispatchId: string;
  DispatchStatus: string;
  AttemptCount: number;
  LastAttemptAt: string | null;
  LastError: string | null;
  CreatedAt: string;
  LockedBy: string | null;
  DispatchResultId: string | null;
  Mode: string | null;
  Success: boolean | null;
  HttpStatus: number | null;
  ResultAt: string | null;
  ResultSummaryJson: string | null;
}

export interface ProductDetail {
  available: true;
  found: boolean;
  payload?: Record<string, unknown> & { OPProductPayloadId: string; EAN: string | null; Title: string | null };
  core?: Record<string, unknown> | null;
  latestAudit?: {
    AuditRunId: string;
    AuditStatus: string;
    MismatchCount: number;
    NotFoundInOP: boolean;
    OPProductId: number | null;
    CreatedAt: string;
    fieldResults: FieldResult[] | null;
  } | null;
  dispatchHistory?: DispatchHistoryRow[];
}

export interface ProductPayloadResponse {
  available: true;
  found: boolean;
  ean?: string;
  payload?: Record<string, unknown>;
  snapshot?: Record<string, unknown> | null;
  merged?: Record<string, unknown> | null;
  snapshotMode?: string | null;
  snapshotAt?: string | null;
}

export interface ProductPricesResponse {
  available: true;
  total: number;
  blocked: number;
  note: string;
  rows: Record<string, unknown>[];
}

export interface MismatchItemRow {
  EAN: string | null;
  OPProductId: number | null;
  OPProductPayloadId: string;
  AuditStatus: string;
  field: string;
  severity: string;
  mode: string;
  expected: string | null;
  actual: string | null;
}

export interface MismatchesResponse {
  available: true;
  fieldSummary: FieldSummaryRow[];
  total: number;
  page: number;
  pageSize: number;
  items: MismatchItemRow[];
}

type Maybe<T> = T | CanonicalUnavailable;

async function getJson<T>(url: string, label: string): Promise<T> {
  const res = await apiRequest('GET', url);
  if (!res.ok) throw new Error(`Failed to fetch ${label}`);
  return res.json();
}

export const fetchOPReadinessSummary = () =>
  getJson<Maybe<ReadinessSummary>>('/api/op-readiness/summary', 'OP readiness summary');

export const fetchOPReadinessProducts = (params: {
  status?: string;
  auditStatus?: string;
  blockedReason?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.auditStatus) q.set('auditStatus', params.auditStatus);
  if (params.blockedReason) q.set('blockedReason', params.blockedReason);
  if (params.search) q.set('search', params.search);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 50));
  return getJson<Maybe<ProductsResponse>>(`/api/op-readiness/products?${q}`, 'OP readiness products');
};

export const fetchOPReadinessProductDetail = (payloadId: string) =>
  getJson<Maybe<ProductDetail>>(`/api/op-readiness/products/${payloadId}`, 'OP readiness product detail');

export const fetchOPReadinessProductPayload = (payloadId: string) =>
  getJson<Maybe<ProductPayloadResponse>>(
    `/api/op-readiness/products/${payloadId}/payload`,
    'OP readiness product payload'
  );

export const fetchOPReadinessProductPrices = (payloadId: string) =>
  getJson<Maybe<ProductPricesResponse>>(
    `/api/op-readiness/products/${payloadId}/prices`,
    'OP readiness product prices'
  );

export const fetchOPReadinessAuditRuns = () =>
  getJson<Maybe<{ available: true; runs: AuditRunRow[] }>>(
    '/api/op-readiness/audit-runs',
    'OP readiness audit runs'
  );

export const fetchOPReadinessMismatches = (
  auditRunId: string,
  params: { severity?: string; field?: string; page?: number; pageSize?: number }
) => {
  const q = new URLSearchParams();
  if (params.severity) q.set('severity', params.severity);
  if (params.field) q.set('field', params.field);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 100));
  return getJson<Maybe<MismatchesResponse>>(
    `/api/op-readiness/audit-runs/${auditRunId}/mismatches?${q}`,
    'OP readiness mismatches'
  );
};

// ---- Stage 7b: dispatch monitoring (read-only) ----

export interface DispatchListRow {
  DispatchId: string;
  EAN: string | null;
  OPProductId: number | null;
  OPProductPayloadId: string;
  DispatchStatus: string;
  AttemptCount: number;
  LastAttemptAt: string | null;
  LastError: string | null;
  DeadLetterAt: string | null;
  DeadLetterReason: string | null;
  IsActiveDispatch: boolean;
  LockedBy: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  Title: string | null;
  Mode: string | null;
  Success: boolean | null;
  HttpStatus: number | null;
  ResultAt: string | null;
}

export interface CountRow {
  DispatchStatus?: string;
  ModeRollup?: string;
  Count: number;
}

export interface DispatchResponse {
  available: true;
  statusRollup: CountRow[];
  modeRollup: CountRow[];
  total: number;
  page: number;
  pageSize: number;
  rows: DispatchListRow[];
}

export interface DispatchResultDetail {
  DispatchResultId: string;
  Mode: string | null;
  Success: boolean | null;
  HttpStatus: number | null;
  ErrorMessage: string | null;
  CreatedAt: string;
  request: Record<string, unknown> | null;
  merged: Record<string, unknown> | null;
  response: Record<string, unknown> | null;
  preReadSnapshot: Record<string, unknown> | null;
}

export interface DispatchDetail {
  available: true;
  found: boolean;
  queue?: Record<string, unknown> & { DispatchId: string; DispatchStatus: string; Title: string | null };
  results?: DispatchResultDetail[];
}

export const fetchOPReadinessDispatch = (params: {
  status?: string;
  mode?: string;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
}) => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.mode) q.set('mode', params.mode);
  if (params.activeOnly) q.set('activeOnly', 'true');
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 50));
  return getJson<Maybe<DispatchResponse>>(`/api/op-readiness/dispatch?${q}`, 'OP readiness dispatch');
};

export const fetchOPReadinessDispatchDetail = (dispatchId: string) =>
  getJson<Maybe<DispatchDetail>>(`/api/op-readiness/dispatch/${dispatchId}`, 'OP readiness dispatch detail');

// ---- Stage 7b: runtime dispatch CONTROL (pause/resume kill-switch) ----

export interface DispatchControlRow {
  target: 'op' | 'hubspot';
  killSwitch: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  note: string | null;
}

export const fetchDispatchControl = () =>
  getJson<{ success: true; controls: DispatchControlRow[] }>(
    '/api/op-readiness/dispatch-control',
    'dispatch control'
  );

/** Pause (killSwitch=true) or resume (false) a loop at runtime. Never enables live writes. */
export async function setDispatchControl(
  target: 'op' | 'hubspot',
  killSwitch: boolean,
  note?: string
): Promise<{ success: true; action: 'PAUSED' | 'RESUMED'; control: DispatchControlRow }> {
  const res = await apiRequest('POST', '/api/op-readiness/dispatch-control', { target, killSwitch, note });
  if (!res.ok) throw new Error('Failed to set dispatch control');
  return res.json();
}

export function isUnavailable<T>(data: Maybe<T> | undefined): data is CanonicalUnavailable {
  return !!data && (data as CanonicalUnavailable).available === false;
}
